import {inject, Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {BehaviorSubject, Observable, of, Subscription, timer} from 'rxjs';
import {catchError, exhaustMap, map} from 'rxjs/operators';
import {BicToastService} from '@buildinginformationcloud/bic-ui-components';
import {RuleTestApi} from './rule-test.api';
import {
  RuleTestErrorResponse,
  RuleTestQuota,
  RuleTestRunView,
  RuleTestStartRequest,
  RuleTestStartResult,
} from './rule-test.models';
import {
  RuleTestToastComponent,
  RuleTestToastData,
} from '../../components/ruleManagement/shared/rule-test-toast/rule-test-toast.component';

/** Abstand der Statusabfragen, solange ein Lauf RUNNING ist. */
export const RULE_TEST_POLL_INTERVAL_MS = 2000;

/** Nach so vielen Fehlschlägen am Stück gibt das Polling auf (Netz weg, Lauf aufgeräumt). */
export const RULE_TEST_MAX_POLL_FAILURES = 5;

/** Zustand eines Editor-Kontexts (`ruleTestContextKey`) in dieser Sitzung. */
interface ContextState {
  readonly run: BehaviorSubject<RuleTestRunView | null>;
  readonly unseen: BehaviorSubject<boolean>;
  /** Stabile Observable-Instanzen – Templates binden sie mit `| async`, ein neues Objekt je Aufruf
   *  ließe die Pipe in jeder Change Detection neu abonnieren. */
  readonly run$: Observable<RuleTestRunView | null>;
  readonly unseen$: Observable<boolean>;
  projectId: number | null;
  poll: Subscription | null;
  failures: number;
  toastId: string | null;
}

interface RegisteredEditor {
  openPopup: () => void;
}

/**
 * Sitzungszustand des Testmodus (AP F1): Tageslimit, laufender Testlauf je Editor-Kontext,
 * Polling bis zum Ende, Toast, wenn das Popup währenddessen geschlossen ist.
 *
 * Root-Service, damit ein Lauf das Schließen des Popups und das Verlassen des Editors überlebt.
 * Ein Reload überlebt er bewusst nicht (Entscheidung E7 im Plan).
 */
@Injectable({providedIn: 'root'})
export class RuleTestService {
  private api = inject(RuleTestApi);
  private toastService = inject(BicToastService);
  private router = inject(Router);

  private readonly quotaSubject = new BehaviorSubject<RuleTestQuota | null>(null);
  private readonly contexts = new Map<string, ContextState>();
  /** Bleibt nach dem Abmelden des Editors erhalten – der Toast-Link braucht die Route auch dann. */
  private readonly editorRoutes = new Map<string, unknown[]>();
  private readonly editors = new Map<string, RegisteredEditor>();
  private popupOpenKey: string | null = null;

  /** Aktuelles Tageslimit; `null`, solange noch nicht geladen. */
  readonly quota$: Observable<RuleTestQuota | null> = this.quotaSubject.asObservable();

  /** Letzter bekannter Stand des Tageslimits (synchron, z. B. für Handler). */
  get quotaSnapshot(): RuleTestQuota | null {
    return this.quotaSubject.value;
  }

  /** Lädt das Tageslimit neu (beim Betreten eines Editors und nach jedem Lauf). Fehler sind still. */
  refreshQuota(): void {
    this.api.getQuota().pipe(catchError(() => of(null))).subscribe(quota => {
      if (quota) {
        this.quotaSubject.next(quota);
      }
    });
  }

  /** Startet einen Testlauf; Fehler kommen als `RuleTestStartResult`, nicht als Fehler im Observable. */
  start(projectId: number, contextKey: string, request: RuleTestStartRequest): Observable<RuleTestStartResult> {
    return this.api.start(projectId, request).pipe(
      map((run): RuleTestStartResult => {
        const ctx = this.context(contextKey);
        ctx.projectId = projectId;
        this.dismissToast(ctx);
        ctx.unseen.next(false);
        this.applyRun(contextKey, run, false);
        return {kind: 'started', run};
      }),
      catchError((error: unknown) => of(this.mapStartError(error))),
    );
  }

  /** Laufender bzw. letzter Testlauf dieses Kontexts in der Sitzung; `null`, wenn es keinen gibt. */
  run$(contextKey: string): Observable<RuleTestRunView | null> {
    return this.context(contextKey).run$;
  }

  /** Synchroner Stand von `run$`. */
  runSnapshot(contextKey: string): RuleTestRunView | null {
    return this.contexts.get(contextKey)?.run.value ?? null;
  }

  /**
   * Lädt einen Lauf (z. B. aus dem Query-Param `ruleTest`) und hängt ihn an den Kontext. Ist er schon
   * bekannt und beendet, passiert nichts. Ein beim Laden bereits beendeter Lauf löst keinen Toast aus –
   * der Nutzer öffnet ihn ja gerade.
   */
  attach(projectId: number, contextKey: string, runId: string): void {
    const ctx = this.context(contextKey);
    ctx.projectId = projectId;
    const known = ctx.run.value;
    if (known?.runId === runId && (known.status !== 'RUNNING' || ctx.poll)) {
      return;
    }
    this.api.get(runId, projectId).pipe(catchError(() => of(null))).subscribe(run => {
      if (run) {
        this.applyRun(contextKey, run, false);
      }
    });
  }

  /** Ob ein Ergebnis bereitliegt, das der Nutzer noch nicht gesehen hat (Punkt am Header-Button). */
  hasUnseenResult$(contextKey: string): Observable<boolean> {
    return this.context(contextKey).unseen$;
  }

  markSeen(contextKey: string): void {
    const ctx = this.contexts.get(contextKey);
    if (ctx?.unseen.value) {
      ctx.unseen.next(false);
    }
  }

  /** Das Popup meldet, für welchen Kontext es offen ist (`null` = geschlossen) – dann kein Toast. */
  setPopupOpen(contextKey: string | null): void {
    this.popupOpenKey = contextKey;
    if (contextKey) {
      this.markSeen(contextKey);
      const ctx = this.contexts.get(contextKey);
      if (ctx) {
        this.dismissToast(ctx);
      }
    }
  }

  /**
   * Der aktive Editor meldet sich an: „Ergebnis ansehen" im Toast öffnet dann direkt sein Popup.
   * Ohne angemeldeten Handler navigiert der Toast zur Editor-Route mit `?ruleTest=<runId>`.
   *
   * @param editorRoute Router-Commands der Editor-Route, z. B. `['projects', 12, 'my-checks', id, 'ki']`
   * @returns Abmelden (in `ngOnDestroy` aufrufen)
   */
  registerEditor(contextKey: string, editorRoute: unknown[], openPopup: () => void): () => void {
    const entry: RegisteredEditor = {openPopup};
    this.editorRoutes.set(contextKey, editorRoute);
    this.editors.set(contextKey, entry);
    return () => {
      if (this.editors.get(contextKey) === entry) {
        this.editors.delete(contextKey);
      }
    };
  }

  // ─── intern ────────────────────────────────────────────────────────────────────────────────

  private context(contextKey: string): ContextState {
    let ctx = this.contexts.get(contextKey);
    if (!ctx) {
      const run = new BehaviorSubject<RuleTestRunView | null>(null);
      const unseen = new BehaviorSubject<boolean>(false);
      ctx = {
        run,
        unseen,
        run$: run.asObservable(),
        unseen$: unseen.asObservable(),
        projectId: null,
        poll: null,
        failures: 0,
        toastId: null,
      };
      this.contexts.set(contextKey, ctx);
    }
    return ctx;
  }

  /**
   * Übernimmt einen Stand vom Server. `observedTransition`: der Lauf war bis eben RUNNING und ist es
   * jetzt nicht mehr – nur dann gibt es Toast bzw. den Punkt am Button.
   */
  private applyRun(contextKey: string, run: RuleTestRunView, observedTransition: boolean): void {
    const ctx = this.context(contextKey);
    ctx.run.next(run);
    if (run.quota) {
      this.quotaSubject.next(run.quota);
    }

    if (run.status === 'RUNNING') {
      this.startPolling(contextKey, ctx, run.runId);
      return;
    }

    this.stopPolling(ctx);
    if (observedTransition) {
      // Der Server stellt den Lauf und die Rückgabe der Reservierung in zwei Schritten um (Watchdog:
      // erst FAILED, dann release). Der letzte Poll kann dazwischen liegen – ohne Nachladen bliebe
      // das Kontingent nach einem Timeout einen Lauf zu niedrig (Frame 07 verspricht „2 von 5").
      this.refreshQuota();
    }
    if (observedTransition && this.popupOpenKey !== contextKey) {
      ctx.unseen.next(true);
      this.showToast(contextKey, ctx, run);
    }
  }

  private startPolling(contextKey: string, ctx: ContextState, runId: string): void {
    if (ctx.poll || ctx.projectId == null) {
      return;
    }
    const projectId = ctx.projectId;
    ctx.failures = 0;
    ctx.poll = timer(RULE_TEST_POLL_INTERVAL_MS, RULE_TEST_POLL_INTERVAL_MS).pipe(
      exhaustMap(() => this.api.get(runId, projectId).pipe(
        catchError((error: unknown) => {
          ctx.failures++;
          // 404: Lauf ist weg (aufgeräumt/fremd) – weiterfragen bringt nichts.
          const gone = error instanceof HttpErrorResponse && error.status === 404;
          if (gone || ctx.failures >= RULE_TEST_MAX_POLL_FAILURES) {
            this.stopPolling(ctx);
          }
          return of(null);
        }),
      )),
    ).subscribe(run => {
      if (!run || ctx.run.value?.runId !== runId) {
        return;
      }
      ctx.failures = 0;
      const wasRunning = ctx.run.value?.status === 'RUNNING';
      this.applyRun(contextKey, run, wasRunning && run.status !== 'RUNNING');
    });
  }

  private stopPolling(ctx: ContextState): void {
    ctx.poll?.unsubscribe();
    ctx.poll = null;
  }

  private showToast(contextKey: string, ctx: ContextState, run: RuleTestRunView): void {
    this.dismissToast(ctx);
    const fileNames = (run.files ?? []).map(f => f.fileName).filter(Boolean).join(', ');
    const data: RuleTestToastData = {
      status: run.status === 'FAILED' ? 'FAILED' : 'COMPLETED',
      ruleLabel: run.ruleLabel ?? '',
      fileNames,
      open: () => this.openResult(contextKey, run.runId),
    };
    const ref = this.toastService.showComponent(RuleTestToastComponent, {
      type: run.status === 'FAILED' ? 'error' : 'success',
      data,
      durationMs: 0,
      closable: true,
    });
    ctx.toastId = ref.id;
  }

  private dismissToast(ctx: ContextState): void {
    if (ctx.toastId) {
      this.toastService.dismiss(ctx.toastId);
      ctx.toastId = null;
    }
  }

  /** „Ergebnis ansehen": Popup des angemeldeten Editors öffnen, sonst zur Editor-Route navigieren. */
  private openResult(contextKey: string, runId: string): void {
    const ctx = this.contexts.get(contextKey);
    if (ctx) {
      ctx.toastId = null; // der Container schließt den Toast beim Klick selbst
    }
    const editor = this.editors.get(contextKey);
    if (editor) {
      editor.openPopup();
      return;
    }
    const route = this.editorRoutes.get(contextKey);
    if (route) {
      // Über einen Umweg navigieren: steht der Nutzer gerade im Editor eines ANDEREN Sets, würde
      // Angular dieselbe Komponente wiederverwenden – die Editoren lesen Set und `ruleTest` nur beim
      // Initialisieren, die Seite bliebe beim falschen Set und das Popup ginge nicht auf.
      void this.router.navigateByUrl('/', {skipLocationChange: true}).then(() =>
        this.router.navigate(route, {queryParams: {ruleTest: runId}}),
      );
    }
  }

  private mapStartError(error: unknown): RuleTestStartResult {
    if (!(error instanceof HttpErrorResponse)) {
      return {kind: 'error', message: 'Der Testlauf konnte nicht gestartet werden.'};
    }
    const body = (error.error ?? null) as Partial<RuleTestErrorResponse> | null;
    switch (error.status) {
      case 422:
        return {kind: 'invalid', errors: body?.errors?.length ? body.errors : [body?.message || 'Testlauf nicht möglich.']};
      case 429:
        if (body?.quota) {
          this.quotaSubject.next(body.quota);
          return {kind: 'limit', quota: body.quota};
        }
        return {kind: 'limit', quota: this.exhaustedQuota()};
      case 409:
        if (body?.runId) {
          return {kind: 'active', runId: body.runId};
        }
        break;
    }
    return {kind: 'error', message: body?.message || 'Der Testlauf konnte nicht gestartet werden.'};
  }

  /** 429 ohne Quota im Body: den letzten Stand als „aufgebraucht" melden. */
  private exhaustedQuota(): RuleTestQuota {
    const last = this.quotaSubject.value;
    const exhausted: RuleTestQuota = last
      ? {...last, used: last.limit, remaining: 0}
      : {limit: 0, used: 0, remaining: 0, resetsAt: '', activeRunId: null};
    this.quotaSubject.next(exhausted);
    return exhausted;
  }
}
